let resolvedApiBaseUrl: string | null = null;
// The build label (e.g. 'pr-5') of the active override, or null when the default URL is in use.
// Tracked so the UI can surface that a non-prod PR build is in effect — including on a reload,
// where the param has been stripped from the URL and only the sessionStorage label remains.
let activeBuildLabel: string | null = null;

const SESSIONSTORE_OVERRIDE_KEY = '__cp_gql_api_override';
const PROD_APEX_HOST = 'gql-api.creditpulse.com';
const VALID_BUILD_REGEX = /^pr-[1-9]\d*$/;

const extractBuildParam = (): string | undefined => {
  const { hash } = window.location;
  const queryStart = hash.indexOf('?');

  if (queryStart !== -1) {
    // slice (not split) so additional '?' chars in a malformed URL stay in the query string
    const queryPortion = hash.slice(queryStart + 1);
    const buildParam = new URLSearchParams(queryPortion).get('build');
    if (buildParam) {
      // trim so a percent-encoded trailing newline (e.g. ?build=pr-1%0A) can't slip past the
      // anchored regex — '$' matches the position before a final '\n' in non-multiline mode.
      return buildParam.trim();
    }
  }

  // The app uses hash history, so the param normally lives after the '#'. We also honor it
  // on the real query string so it works whether a user types ?build= before or after the
  // hash. Safe because the whole override mechanism is prod-gated in initializeApiBaseUrl.
  const fallbackBuildParam = new URLSearchParams(window.location.search).get('build');
  return fallbackBuildParam?.trim() ?? undefined;
};

const isValidBuildParam = (value: string): boolean => {
  return VALID_BUILD_REGEX.test(value);
};

const isProdEnvironment = (defaultUrl: string): boolean => {
  try {
    const hostname = new URL(defaultUrl).hostname;
    return hostname === PROD_APEX_HOST;
  } catch {
    // Fail closed: an absent or malformed defaultUrl is treated as prod so a
    // misconfigured deploy can never enable a build override. Warn so local dev with a
    // missing env var can tell why the override was suppressed.
    console.warn(
      'Could not parse API base URL; treating environment as production and ignoring any build override.',
    );
    return true;
  }
};

const resolveBuildToUrl = (buildParam: string): string => {
  // HARDCODED TEMPLATE: no derivation from current URL
  return `https://${buildParam}.gql-api.creditpulse.com/${buildParam}/graphql`;
};

const safeSessionStorageWrite = (key: string, value: string): void => {
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Silently fail if sessionStorage is unavailable (private browsing, etc.)
  }
};

const safeSessionStorageRead = (key: string): string | null => {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    // Silently fail if sessionStorage is unavailable
    return null;
  }
};

const safeSessionStorageRemove = (key: string): void => {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Silently fail if sessionStorage is unavailable
  }
};

export const initializeApiBaseUrl = (defaultUrl: string): void => {
  // Idempotent: once resolved, a repeat call (HMR re-eval, or any future second caller) must
  // not recompute from a now-cleaned URL and silently drop an active override. Callers that
  // genuinely need to re-resolve must call resetApiBaseUrl() first (the test pattern).
  if (resolvedApiBaseUrl !== null) {
    return;
  }

  resolvedApiBaseUrl = defaultUrl;

  if (!resolvedApiBaseUrl) {
    // An empty base produces relative request URLs that silently hit window.location.origin.
    console.warn('API base URL is not set; API requests will use a relative base URL.');
  }

  if (isProdEnvironment(defaultUrl)) {
    safeSessionStorageRemove(SESSIONSTORE_OVERRIDE_KEY);
    return;
  }

  const buildParam = extractBuildParam();

  if (buildParam) {
    if (!isValidBuildParam(buildParam)) {
      console.warn(
        `Invalid build parameter: ${buildParam}. Expected format: pr-<number>. Using default API URL.`,
      );
      // An explicit invalid param is a signal to stop overriding — clear any stale stored
      // override so it isn't re-applied on the next init (e.g. a reload that drops the param).
      safeSessionStorageRemove(SESSIONSTORE_OVERRIDE_KEY);
      return;
    }

    resolvedApiBaseUrl = resolveBuildToUrl(buildParam);
    activeBuildLabel = buildParam;
    // Persist the build label (not the resolved URL) so read-back is re-validated and
    // re-resolved below — a tampered sessionStorage value can never become the API base.
    safeSessionStorageWrite(SESSIONSTORE_OVERRIDE_KEY, buildParam);
    return;
  }

  const storedBuildParam = safeSessionStorageRead(SESSIONSTORE_OVERRIDE_KEY);
  if (storedBuildParam && isValidBuildParam(storedBuildParam)) {
    resolvedApiBaseUrl = resolveBuildToUrl(storedBuildParam);
    activeBuildLabel = storedBuildParam;
  } else if (storedBuildParam) {
    // Stored value is not a valid build label (stale or tampered) — drop it.
    safeSessionStorageRemove(SESSIONSTORE_OVERRIDE_KEY);
  }
};

export const getApiBaseUrl = (): string => {
  if (resolvedApiBaseUrl === null) {
    initializeApiBaseUrl(process.env.REACT_APP_API_BASE_URL ?? '');
  }

  return resolvedApiBaseUrl ?? '';
};

export interface ActiveBuildOverride {
  label: string;
  url: string;
}

// Returns the active gql-api build override, or null when the default API URL is in use.
// Drives the sidebar indicator that tells a developer a non-prod PR build is being targeted.
// Lazily initializes on first read, mirroring getApiBaseUrl, so it is safe to call before
// any explicit initializeApiBaseUrl (e.g. during the first render).
export const getActiveBuildOverride = (): ActiveBuildOverride | null => {
  if (resolvedApiBaseUrl === null) {
    initializeApiBaseUrl(process.env.REACT_APP_API_BASE_URL ?? '');
  }

  if (!activeBuildLabel) {
    return null;
  }

  return {
    label: activeBuildLabel,
    url: resolvedApiBaseUrl ?? '',
  };
};

// Removes the dev-only ?build= override from both the real query string and the hash-router
// query (e.g. #/route?build=pr-5) via history.replaceState. initializeApiBaseUrl has already
// read and persisted the param, so removing it does not disable the override. Call once at
// startup AFTER initializeApiBaseUrl and BEFORE the router is created — hash history captures
// the hash into router state, which would otherwise re-add ?build= on the next navigation.
export const stripBuildParamFromUrl = (): void => {
  try {
    const {
      origin, pathname, search, hash,
    } = window.location;

    const searchParams = new URLSearchParams(search);
    const searchHadBuild = searchParams.has('build');
    if (searchHadBuild) {
      searchParams.delete('build');
    }

    let newHash = hash;
    const hashQueryStart = hash.indexOf('?');
    if (hashQueryStart !== -1) {
      const hashPath = hash.slice(0, hashQueryStart);
      const hashParams = new URLSearchParams(hash.slice(hashQueryStart + 1));
      if (hashParams.has('build')) {
        hashParams.delete('build');
        const remainingHashQuery = hashParams.toString();
        newHash = remainingHashQuery ? `${hashPath}?${remainingHashQuery}` : hashPath;
      }
    }

    if (!searchHadBuild && newHash === hash) {
      return;
    }

    const newSearch = searchParams.toString();
    const newUrl = `${origin}${pathname}${newSearch ? `?${newSearch}` : ''}${newHash}`;
    window.history.replaceState(window.history.state, '', newUrl);
  } catch {
    // Best-effort cleanup; never block startup on a URL-rewrite failure.
  }
};

// Test-only: clears the module cache and stored override for test isolation. Do NOT call
// from application code — resetting mid-session would drop the override on the next
// getApiBaseUrl() call if the URL no longer carries the build param. Note: logout clears
// sessionStorage, so the build override must be re-added after re-authentication.
export const resetApiBaseUrl = (): void => {
  resolvedApiBaseUrl = null;
  activeBuildLabel = null;
  safeSessionStorageRemove(SESSIONSTORE_OVERRIDE_KEY);
};
